import React, { useState, useCallback } from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  Alert,
  Table,
  Badge,
  FileInput,
  LoadingOverlay,
  Checkbox,
  Divider,
  Tooltip,
  MultiSelect,
  Paper,
  Progress
} from '@mantine/core';
import {
  IconDownload,
  IconUpload,
  IconFileSpreadsheet,
  IconCheck,
  IconX,
  IconInfoCircle,
  IconPlus
} from '@tabler/icons-react';
import * as XLSX from 'xlsx';
import httpClient from 'services/httpClient';

interface Module {
  moduleId: string;
  moduleName: string;
  courseCode?: string;
  description?: string;
}

interface ImportedParticipant {
  binusianId: string;
  name: string;
  email: string;
  division: string;
  type: string;
  listModule: string[];
  isValid: boolean;
  errors: string[];
  isVerified?: boolean;
  verificationMessage?: string;
  binusianName?: string | null;
  binusianEmail?: string | null;
}

/* eslint-disable */
interface ImportParticipantsModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (participants: ImportedParticipant[]) => void;
  availableModules: Module[];
  statusKurikulum?: boolean;
}
/* eslint-enable */

const ImportParticipantsModal: React.FC<ImportParticipantsModalProps> = ({
  opened,
  onClose,
  onSubmit,
  availableModules,
  statusKurikulum = false
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importedData, setImportedData] = useState<ImportedParticipant[]>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  const [step, setStep] = useState<'upload' | 'preview' | 'result'>('upload');
  const [bulkCourses, setBulkCourses] = useState<string[]>([]);
  const [verificationProgress, setVerificationProgress] = useState(0);
  const [verificationTotal, setVerificationTotal] = useState(0);
  const [verificationMessage, setVerificationMessage] = useState('');

  // Reset modal state when opened/closed
  React.useEffect(() => {
    if (opened) {
      setFile(null);
      setImportedData([]);
      setSelectedParticipants([]);
      setStep('upload');
      setIsProcessing(false);
      setBulkCourses([]);
      setVerificationProgress(0);
      setVerificationTotal(0);
      setVerificationMessage('');
    }
  }, [opened]);

  // Generate and download Excel template
  const downloadTemplate = () => {
    // Main template data with example participants
    const templateData = [
      {
        'Binusian ID': 'B001234567',
        Name: 'John Doe',
        Email: 'john.doe@binus.ac.id',
        Division: 'Computer Science',
        Type: 'Employee'
      },
      {
        'Binusian ID': 'B001234568',
        Name: 'Jane Smith',
        Email: 'jane.smith@binus.ac.id',
        Division: 'Information Systems',
        Type: 'Lecturer'
      }
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Add main template sheet
    const wsTemplate = XLSX.utils.json_to_sheet(templateData);
    wsTemplate['!cols'] = [
      { width: 15 }, // Binusian ID
      { width: 20 }, // Name
      { width: 25 }, // Email
      { width: 20 }, // Division
      { width: 12 } // Type
    ];
    XLSX.utils.book_append_sheet(wb, wsTemplate, 'Template');

    // Write file
    XLSX.writeFile(wb, 'participants_import_template.xlsx');
  };

  // Validate participant data
  const validateParticipant = (row: any): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!row['Binusian ID'] || String(row['Binusian ID']).trim() === '') {
      errors.push('Binusian ID is required');
    }

    if (!row['Name'] || String(row['Name']).trim() === '') {
      errors.push('Name is required');
    }

    if (!row['Email'] || String(row['Email']).trim() === '') {
      errors.push('Email is required');
    } else {
      const email = String(row['Email']).trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        errors.push('Invalid email format');
      }
    }

    if (!row['Type'] || String(row['Type']).trim() === '') {
      errors.push('Type is required');
    }

    return { isValid: errors.length === 0, errors };
  };

  // Process uploaded Excel file
  const processFile = useCallback(async () => {
    if (!file) return;

    setIsProcessing(true);
    try {
      let rawData: any[] = [];

      // Check file type and process accordingly
      if (file.name.endsWith('.csv')) {
        // Process CSV file
        const text = await file.text();
        const lines = text.split('\n').filter((line) => !line.startsWith('#') && line.trim());

        if (lines.length < 2) {
          throw new Error('CSV file is empty or invalid');
        }

        const headers = lines[0].split(',').map((h) => h.replace(/^"|"$/g, '').trim());

        rawData = lines.slice(1).map((line) => {
          const values = line.match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g) || [];
          const row: any = {};
          headers.forEach((header, index) => {
            row[header] = values[index] ? values[index].replace(/^"|"$/g, '').trim() : '';
          });
          return row;
        });
      } else {
        // Process Excel file
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        rawData = XLSX.utils.sheet_to_json(worksheet);
      }

      const processedData: ImportedParticipant[] = rawData.map((row: any) => {
        const validation = validateParticipant(row);

        return {
          binusianId: String(row['Binusian ID'] || '').trim(),
          name: String(row['Name'] || '').trim(),
          email: String(row['Email'] || '').trim(),
          division: String(row['Division'] || '').trim(),
          type: String(row['Type'] || '').trim(),
          listModule: [],
          isValid: validation.isValid,
          errors: validation.errors,
          isVerified: false,
          verificationMessage: 'Pending verification'
        };
      });

      // Verify binusianIds with backend in batches of 20
      const BATCH_SIZE = 20;
      const totalParticipants = processedData.length;
      setVerificationTotal(totalParticipants);
      setVerificationProgress(0);
      setVerificationMessage(`Verifying participants... 0/${totalParticipants}`);

      try {
        const allVerificationResults: any[] = [];

        // Split into batches
        for (let i = 0; i < processedData.length; i += BATCH_SIZE) {
          const batch = processedData.slice(i, i + BATCH_SIZE);
          const batchPayload = batch.map((p) => ({
            binusianId: p.binusianId,
            type: p.type,
            email: p.email
          }));

          // Update progress message
          const currentBatch = Math.min(i + BATCH_SIZE, totalParticipants);
          setVerificationMessage(`Verifying participants... ${currentBatch}/${totalParticipants}`);

          try {
            const verificationResponse = await httpClient.post(
              '/employee/verify-bulk',
              batchPayload
            );
            const batchResults = verificationResponse.data?.data || [];
            allVerificationResults.push(...batchResults);

            // Update progress
            setVerificationProgress(currentBatch);
          } catch (batchError: any) {
            alert(
              batchError?.response?.data?.message || 'Verification service is currently unavailable'
            );
            const failedBatch = batch.map((p) => ({
              binusianId: p.binusianId,
              binusianName: null,
              binusianEmail: null,
              isValid: false,
              message: 'Verification failed for this batch'
            }));
            allVerificationResults.push(...failedBatch);
            setVerificationProgress(currentBatch);
          }
        }

        setVerificationMessage('Processing verification results...');

        // Update processedData with verification results
        const verifiedData = processedData.map((participant) => {
          const verificationResult = allVerificationResults.find(
            (v: any) => v.binusianId === participant.binusianId
          );

          if (verificationResult) {
            const isFullyValid = participant.isValid && verificationResult.isValid;
            const errors = [...participant.errors];

            if (!verificationResult.isValid) {
              errors.push(verificationResult.message || 'Binusian not found');
            }

            return {
              ...participant,
              isValid: isFullyValid,
              errors,
              isVerified: verificationResult.isValid,
              verificationMessage: verificationResult.message,
              binusianName: verificationResult.binusianName,
              binusianEmail: verificationResult.binusianEmail,
              // Update name and email with verified data if available
              name: verificationResult.binusianName || participant.name,
              email: participant.email
            };
          }

          return {
            ...participant,
            isValid: false,
            errors: [...participant.errors, 'Verification failed'],
            isVerified: false,
            verificationMessage: 'Verification failed'
          };
        });

        setImportedData(verifiedData);
        setSelectedParticipants(verifiedData.filter((p) => p.isValid).map((p) => p.binusianId));
        setVerificationMessage('Verification complete!');
      } catch (error: any) {
        alert(error?.response?.data?.message || 'Verification service is currently unavailable');
        const unverifiedData = processedData.map((p) => ({
          ...p,
          isValid: false,
          errors: [...p.errors, 'Could not verify Binusian ID'],
          isVerified: false,
          verificationMessage: 'Verification service unavailable'
        }));
        setImportedData(unverifiedData);
        setSelectedParticipants([]);
        setVerificationMessage('Verification failed');
      }

      setStep('preview');
    } catch {
      setImportedData([]);
      setSelectedParticipants([]);
      setStep('upload');
      alert('Failed to process the file. Please ensure it is a valid Excel or CSV file.');
    } finally {
      setIsProcessing(false);
    }
  }, [file, availableModules, statusKurikulum]);

  // Toggle participant selection
  const toggleParticipant = (binusianId: string) => {
    setSelectedParticipants((prev) =>
      prev.includes(binusianId) ? prev.filter((id) => id !== binusianId) : [...prev, binusianId]
    );
  };

  // Toggle all valid participants
  const toggleAllValid = () => {
    const validParticipants = importedData.filter((p) => p.isValid).map((p) => p.binusianId);
    setSelectedParticipants((prev) =>
      prev.length === validParticipants.length ? [] : validParticipants
    );
  };

  // Submit selected participants
  const handleSubmit = () => {
    const selectedData = importedData.filter(
      (p) => selectedParticipants.includes(p.binusianId) && p.isValid
    );
    onSubmit(selectedData);
    onClose();
  };

  // Get statistics
  const stats = {
    total: importedData.length,
    valid: importedData.filter((p) => p.isValid).length,
    invalid: importedData.filter((p) => !p.isValid).length,
    selected: selectedParticipants.length
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title='Import Participants'
      size='xl'
      padding='lg'
      closeOnClickOutside={!isProcessing}
      closeOnEscape={!isProcessing}
    >
      <LoadingOverlay
        visible={isProcessing}
        loaderProps={{
          children: (
            <Stack align='center' gap='md'>
              <Text size='lg' fw={500}>
                {verificationMessage || 'Processing file...'}
              </Text>
              {verificationTotal > 0 && (
                <>
                  <Progress
                    value={(verificationProgress / verificationTotal) * 100}
                    size='xl'
                    radius='md'
                    striped
                    animated
                    style={{ width: '300px' }}
                  />
                  <Text size='sm' c='dimmed'>
                    {verificationProgress} / {verificationTotal} participants verified
                  </Text>
                </>
              )}
            </Stack>
          )
        }}
      />

      <Stack gap='md'>
        {step === 'upload' && (
          <>
            <Alert icon={<IconInfoCircle size={16} />} color='blue'>
              Upload an Excel file (.xlsx) with participant data. Download the template to see the
              correct format. You can assign {statusKurikulum ? 'courses' : 'modules'} after
              uploading.
            </Alert>

            <Group justify='center'>
              <Button
                leftSection={<IconDownload size={16} />}
                variant='outline'
                onClick={downloadTemplate}
              >
                Download Template
              </Button>
            </Group>

            <Divider />

            <FileInput
              label='Upload Participants File'
              placeholder='Choose Excel (.xlsx) file'
              accept='.xlsx,.xls,.csv'
              leftSection={<IconFileSpreadsheet size={16} />}
              value={file}
              onChange={setFile}
            />

            <Group justify='flex-end'>
              <Button variant='outline' onClick={onClose} disabled={isProcessing}>
                Cancel
              </Button>
              <Button
                leftSection={<IconUpload size={16} />}
                onClick={processFile}
                disabled={!file || isProcessing}
              >
                Process File
              </Button>
            </Group>
          </>
        )}

        {step === 'preview' && (
          <>
            <Group justify='space-between'>
              <div>
                <Text fw={600}>Import Preview</Text>
                <Text size='sm' c='dimmed'>
                  Review and select participants to import
                </Text>
              </div>
              <Group gap='xs'>
                <Badge color='green' variant='light'>
                  {stats.valid} Valid
                </Badge>
                <Badge color='red' variant='light'>
                  {stats.invalid} Invalid
                </Badge>
                <Badge color='blue' variant='light'>
                  {stats.selected} Selected
                </Badge>
              </Group>
            </Group>

            {stats.total > 0 && (
              <>
                {/* Bulk Course Assignment */}
                <Paper withBorder p='md' radius='md' bg='gray.0'>
                  <Stack gap='sm'>
                    <Text size='sm' fw={500}>
                      Bulk Course Assignment
                    </Text>
                    <Group gap='sm' align='flex-end'>
                      <MultiSelect
                        flex={1}
                        placeholder={`Select ${statusKurikulum ? 'courses' : 'modules'} to apply`}
                        data={availableModules.map((m) => ({
                          value: m.moduleId,
                          label: `${m.moduleName}`
                        }))}
                        value={bulkCourses}
                        onChange={setBulkCourses}
                        searchable
                        clearable
                      />
                      <Button
                        variant='light'
                        leftSection={<IconPlus size={16} />}
                        onClick={() => {
                          if (bulkCourses.length === 0) return;

                          setImportedData((prev) =>
                            prev.map((p) => ({
                              ...p,
                              listModule: p.isValid ? bulkCourses : p.listModule
                            }))
                          );
                          setBulkCourses([]);
                        }}
                        disabled={bulkCourses.length === 0}
                      >
                        Apply to All
                      </Button>
                    </Group>
                    <Text size='xs' c='dimmed'>
                      Apply the selected {statusKurikulum ? 'courses' : 'modules'} to all
                      participants at once.
                    </Text>
                  </Stack>
                </Paper>

                <Group justify='space-between'>
                  <Checkbox
                    label={`Select all valid participants (${stats.valid})`}
                    checked={selectedParticipants.length === stats.valid}
                    indeterminate={
                      selectedParticipants.length > 0 && selectedParticipants.length < stats.valid
                    }
                    onChange={toggleAllValid}
                  />
                  <Text size='sm'>
                    {stats.selected} of {stats.valid} selected
                  </Text>
                </Group>

                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: 50 }}>Select</Table.Th>
                        <Table.Th>Participant</Table.Th>
                        <Table.Th>Email</Table.Th>
                        <Table.Th>Type</Table.Th>
                        <Table.Th>{statusKurikulum ? 'Courses' : 'Modules'}</Table.Th>
                        <Table.Th>Status</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {importedData.map((participant, index) => (
                        <Table.Tr
                          key={index}
                          style={{
                            backgroundColor: !participant.isValid ? '#ffebee' : undefined
                          }}
                        >
                          <Table.Td>
                            <Checkbox
                              checked={selectedParticipants.includes(participant.binusianId)}
                              onChange={() => toggleParticipant(participant.binusianId)}
                              disabled={!participant.isValid}
                            />
                          </Table.Td>
                          <Table.Td>
                            <div>
                              <Text size='sm' fw={500}>
                                {participant.name || 'N/A'}
                              </Text>
                              <Text size='xs' c='dimmed'>
                                {participant.binusianId || 'N/A'}
                              </Text>
                              <Text size='xs' c='dimmed'>
                                {participant.division || 'N/A'}
                              </Text>
                            </div>
                          </Table.Td>
                          <Table.Td>
                            <Text size='sm'>{participant.email || 'N/A'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size='sm'>{participant.type || 'N/A'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <MultiSelect
                              placeholder={`Select ${statusKurikulum ? 'courses' : 'modules'}`}
                              data={availableModules.map((m) => ({
                                value: m.moduleId,
                                label: `${m.moduleName}`
                              }))}
                              value={participant.listModule}
                              onChange={(value) => {
                                setImportedData((prev) =>
                                  prev.map((p, i) =>
                                    i === index ? { ...p, listModule: value } : p
                                  )
                                );
                              }}
                              searchable
                              clearable
                              disabled={!participant.isValid}
                              size='xs'
                            />
                          </Table.Td>
                          <Table.Td>
                            <Stack gap={4}>
                              {participant.isValid ? (
                                <Badge
                                  color='green'
                                  size='sm'
                                  leftSection={<IconCheck size={12} />}
                                >
                                  Valid
                                </Badge>
                              ) : (
                                <Tooltip label={participant.errors.join(', ')}>
                                  <Badge color='red' size='sm' leftSection={<IconX size={12} />}>
                                    Invalid
                                  </Badge>
                                </Tooltip>
                              )}
                              {participant.isVerified !== undefined && (
                                <Badge
                                  color={participant.isVerified ? 'teal' : 'orange'}
                                  size='xs'
                                  variant='light'
                                >
                                  {participant.isVerified ? 'Verified' : 'Not Verified'}
                                </Badge>
                              )}
                            </Stack>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>

                <Group justify='space-between'>
                  <Button variant='outline' onClick={() => setStep('upload')}>
                    Back to Upload
                  </Button>
                  <Group>
                    <Button variant='outline' onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      leftSection={<IconCheck size={16} />}
                      onClick={handleSubmit}
                      disabled={selectedParticipants.length === 0}
                    >
                      Import {selectedParticipants.length} Participant
                      {selectedParticipants.length === 1 ? '' : 's'}
                    </Button>
                  </Group>
                </Group>
              </>
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default ImportParticipantsModal;
